import { useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import type { UserProfileResponse } from "@bw/shared/types/generated";
import { GuardianLinkStep } from "./GuardianLinkStep";

interface AgeOutcomeProps {
  profile: UserProfileResponse;
}

/**
 * Server-owned COPPA outcome. Any value other than explicit `active` stays in
 * the parent-approval path, and replacing the form moves focus into this panel.
 */
export function AgeOutcome({ profile }: AgeOutcomeProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  const pending = profile.account_status !== "active";

  if (pending) {
    return (
      <Paper
        ref={panelRef}
        component="section"
        variant="outlined"
        tabIndex={-1}
        aria-label="Account approval status"
        sx={{ p: { xs: 2.25, sm: 3 } }}
      >
        <Stack spacing={2.25}>
          <Chip
            label="Waiting for a guardian"
            color="warning"
            size="small"
            role="status"
            aria-live="polite"
            sx={{ alignSelf: "flex-start" }}
          />
          <GuardianLinkStep />
          {/* Look-only mode (#2691): browsing is open while a guardian decides. */}
          <Button component={RouterLink} to="/" variant="text" size="small" sx={{ alignSelf: "flex-start" }}>
            Look around for now
          </Button>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      ref={panelRef}
      component="section"
      variant="outlined"
      tabIndex={-1}
      aria-label="Account approval status"
      role="status"
      aria-live="polite"
      sx={{ p: { xs: 2.25, sm: 3 } }}
    >
      <Stack spacing={1.5} sx={{ alignItems: "flex-start" }}>
        <CheckCircleRounded color="success" aria-hidden="true" />
        <Typography component="h2" variant="h2">
          You&apos;re good to play
        </Typography>
        <Typography color="text.secondary">
          Your account is active. Let&apos;s find you a court.
        </Typography>
      </Stack>
    </Paper>
  );
}
